import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemBanco } from '../mensagemBanco';
import { iniciaisDe } from '../montagem';
import Modal from '@/components/ui/Modal';
import Botao from '@/components/ui/Botao';
import Campo from '@/components/ui/Campo';
import FolhaCancelarPresencaFrequencia from './FolhaCancelarPresencaFrequencia';
import {
  MOTIVOS_JUSTIFICAR,
  dataHoraCurta,
  ddmm,
  ehPresencaCancelada,
  formatarTelefone,
  hhmm,
  linkWhatsapp,
  motivoCancelamento,
  motivoJustificarTexto,
  motivoServirTexto,
  nomeOu,
  textoQuemMarcou,
} from '../frequencia';
import type {
  FormacaoFrequencia,
  HistoricoServir,
  MotivoJustificar,
  MotivoServir,
  PessoaFrequencia,
} from '../types';

interface Props {
  pessoa: PessoaFrequencia;
  fotoUrl: string | null;
  aoFechar: () => void;
  aoRecarregar: () => Promise<void>;
}

const OPCOES_SERVIR: { valor: MotivoServir; rotulo: string }[] = [
  { valor: 'nao_veio', rotulo: 'Não veio às formações' },
  { valor: 'pediu', rotulo: 'Pediu para não servir' },
  { valor: 'outro', rotulo: 'Outro' },
];

export default function FolhaFrequencia({ pessoa, fotoUrl, aoFechar, aoRecarregar }: Props) {
  const [salvando, setSalvando] = useState<string | null>(null);
  const [fotoGrande, setFotoGrande] = useState(false);

  const [cancelando, setCancelando] = useState<FormacaoFrequencia | null>(null);
  const [salvandoCancel, setSalvandoCancel] = useState(false);
  const [erroCancel, setErroCancel] = useState<string | null>(null);
  const [desfazerCancelarPara, setDesfazerCancelarPara] = useState<number | null>(null);

  const [motivos, setMotivos] = useState<Record<number, MotivoJustificar>>({});
  const [obsJust, setObsJust] = useState<Record<number, string>>({});
  const [desfazerPara, setDesfazerPara] = useState<number | null>(null);
  const [mostrarMotivos, setMostrarMotivos] = useState<Record<number, boolean>>({});

  const [tirarAberto, setTirarAberto] = useState(false);
  const [motivoServir, setMotivoServir] = useState<MotivoServir | null>(null);
  const [obsServir, setObsServir] = useState('');

  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [historico, setHistorico] = useState<HistoricoServir[] | null>(null);

  async function rpc(nome: string, params: Record<string, unknown>) {
    const { data, error } = await supabase.rpc(nome, params);
    if (error) throw error;
    return data as { ok?: boolean; erro?: string; mensagem?: string } | null;
  }

  async function justificar(encontroId: number) {
    const motivo = motivos[encontroId];
    if (!motivo) {
      mostrarToast('Escolha o motivo da falta.', 3500, 'aviso');
      return;
    }
    const texto = (obsJust[encontroId] || '').trim();
    if (motivo === 'outro' && !texto) {
      mostrarToast('Escreva o motivo da falta.', 3500, 'aviso');
      return;
    }
    setSalvando(`just-${encontroId}`);
    try {
      const resp = await rpc('justificar_falta', {
        p_encontro: encontroId,
        p_inscricao: pessoa.inscricao_id,
        p_motivo: motivo,
        p_obs: motivo === 'outro' ? texto : null,
      });
      if (!resp || resp.ok === false) {
        mostrarToast(resp?.erro || 'Não foi possível justificar. Tente de novo.', 4500, 'erro');
        return;
      }
      mostrarToast('Falta justificada', 3500, 'sucesso');
      setMostrarMotivos((m) => ({ ...m, [encontroId]: false }));
      await aoRecarregar();
    } catch (e) {
      mostrarToast(mensagemBanco(e), 4500, 'erro');
    } finally {
      setSalvando(null);
    }
  }

  async function desfazer(encontroId: number) {
    setSalvando(`desf-${encontroId}`);
    try {
      const resp = await rpc('desfazer_justificativa', {
        p_encontro: encontroId,
        p_inscricao: pessoa.inscricao_id,
      });
      if (!resp || resp.ok === false) {
        mostrarToast(resp?.erro || 'Não foi possível desfazer. Tente de novo.', 4500, 'erro');
        return;
      }
      mostrarToast('Justificativa desfeita', 3500, 'sucesso');
      setDesfazerPara(null);
      await aoRecarregar();
    } catch (e) {
      mostrarToast(mensagemBanco(e), 4500, 'erro');
    } finally {
      setSalvando(null);
    }
  }

  async function marcarPresenca(encontroId: number) {
    setSalvando(`marcar-${encontroId}`);
    try {
      const resp = await rpc('marcar_presenca_manual', {
        p_encontro: encontroId,
        p_inscricao: pessoa.inscricao_id,
        p_obs: null,
      });
      if (!resp || resp.ok === false) {
        mostrarToast(resp?.erro || 'Não foi possível marcar. Tente de novo.', 4500, 'erro');
        return;
      }
      mostrarToast('Presença marcada', 3500, 'sucesso');
      await aoRecarregar();
    } catch (e) {
      mostrarToast(mensagemBanco(e), 4500, 'erro');
    } finally {
      setSalvando(null);
    }
  }

  async function cancelarPresenca(motivo: string) {
    if (!cancelando) return;
    if (typeof cancelando.presenca_id !== 'number') {
      setErroCancel('Não foi possível identificar esta presença. Atualize a tela e tente de novo.');
      return;
    }
    setSalvandoCancel(true);
    setErroCancel(null);
    try {
      const resp = await rpc('cancelar_presenca', {
        p_presenca: cancelando.presenca_id,
        p_motivo: motivo,
      });
      if (!resp || resp.ok === false) {
        setErroCancel(resp?.erro || 'Não foi possível cancelar. Tente de novo.');
        return;
      }
      mostrarToast(resp.mensagem || 'Presença cancelada', 3500, 'sucesso');
      setCancelando(null);
      await aoRecarregar();
    } catch (e) {
      setErroCancel(mensagemBanco(e));
    } finally {
      setSalvandoCancel(false);
    }
  }

  async function desfazerCancelamento(f: FormacaoFrequencia) {
    if (typeof f.presenca_id !== 'number') {
      mostrarToast('Não foi possível identificar esta presença.', 4500, 'erro');
      return;
    }
    setSalvando(`desf-${f.encontro_id}`);
    try {
      const resp = await rpc('desfazer_cancelamento_presenca', { p_presenca: f.presenca_id });
      if (!resp || resp.ok === false) {
        mostrarToast(resp?.erro || 'Não foi possível desfazer. Tente de novo.', 4500, 'erro');
        return;
      }
      mostrarToast(resp.mensagem || 'Presença restaurada', 3500, 'sucesso');
      setDesfazerCancelarPara(null);
      await aoRecarregar();
    } catch (e) {
      mostrarToast(mensagemBanco(e), 4500, 'erro');
    } finally {
      setSalvando(null);
    }
  }

  async function servir(pode: boolean) {
    let motivo: MotivoServir | null = null;
    let texto: string | null = null;
    if (!pode) {
      if (!motivoServir) {
        mostrarToast('Escolha o motivo.', 3500, 'aviso');
        return;
      }
      texto = obsServir.trim();
      if (motivoServir === 'outro' && !texto) {
        mostrarToast('Escreva o motivo.', 3500, 'aviso');
        return;
      }
      motivo = motivoServir;
    }
    setSalvando(pode ? 'servir-on' : 'servir-off');
    try {
      const resp = await rpc('definir_servir', {
        p_inscricao: pessoa.inscricao_id,
        p_pode_servir: pode,
        p_motivo: motivo,
        p_obs: texto,
      });
      if (!resp || resp.ok === false) {
        mostrarToast(resp?.erro || 'Não foi possível salvar. Tente de novo.', 4500, 'erro');
        return;
      }
      mostrarToast(pode ? 'Voltou para a montagem' : 'Tirado da montagem', 3500, 'sucesso');
      setTirarAberto(false);
      setMotivoServir(null);
      setObsServir('');
      await aoRecarregar();
    } catch (e) {
      mostrarToast(mensagemBanco(e), 4500, 'erro');
    } finally {
      setSalvando(null);
    }
  }

  async function abrirHistorico() {
    if (historicoAberto) {
      setHistoricoAberto(false);
      return;
    }
    setHistoricoAberto(true);
    if (historico !== null) return;
    try {
      const { data, error } = await supabase.rpc('historico_servir', {
        p_inscricao: pessoa.inscricao_id,
      });
      if (error) throw error;
      setHistorico((data as HistoricoServir[]) || []);
    } catch (e) {
      mostrarToast(mensagemBanco(e), 4500, 'erro');
      setHistorico([]);
    }
  }

  function blocoFormacao(f: FormacaoFrequencia) {
    const cancelada = ehPresencaCancelada(f);
    const ehFalta =
      !cancelada &&
      (f.situacao === 'falta' || f.situacao === 'aguardando' || f.situacao === 'recusada');
    const mostrar = mostrarMotivos[f.encontro_id] === true;

    return (
      <div key={f.encontro_id} className="rounded-2xl border border-secondary-100 bg-white p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="min-w-0 truncate text-sm font-semibold text-foreground-950">{f.nome}</p>
          <span className="shrink-0 text-xs text-secondary-500">{ddmm(f.quando)}</span>
        </div>

        {f.situacao === 'presente' && (
          <div className="mt-1">
            <p className="flex items-center gap-1.5 text-sm font-medium text-success-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-check-line" />
              </span>
              Presente
            </p>
            <p className="mt-0.5 text-xs text-foreground-600">{textoQuemMarcou(f)}</p>
            {typeof f.presenca_id === 'number' && (
              <Botao
                variante="perigo-contorno"
                icone="ri-close-circle-line"
                className="mt-2 h-9 px-3 text-xs"
                onClick={() => {
                  setErroCancel(null);
                  setCancelando(f);
                }}
              >
                Cancelar presença
              </Botao>
            )}
          </div>
        )}

        {cancelada && (
          <div className="mt-1">
            <p className="flex items-center gap-1.5 text-sm font-medium text-foreground-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-forbid-line" />
              </span>
              Cancelada
            </p>
            {motivoCancelamento(f) && (
              <p className="mt-0.5 text-xs text-foreground-600">{motivoCancelamento(f)}</p>
            )}
            <p className="text-xs text-foreground-500">
              por {nomeOu(f.avaliada_por_nome, 'direção')}
              {f.avaliada_em ? ` em ${dataHoraCurta(f.avaliada_em)}` : ''}
            </p>
            {desfazerCancelarPara === f.encontro_id ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-600">
                  A presença de {pessoa.nome.split(' ')[0]} volta a valer?
                </span>
                <Botao
                  variante="secundario"
                  className="h-9 px-3 text-xs"
                  carregando={salvando === `desf-${f.encontro_id}`}
                  onClick={() => desfazerCancelamento(f)}
                >
                  Sim, desfazer
                </Botao>
                <button
                  type="button"
                  onClick={() => setDesfazerCancelarPara(null)}
                  className="cursor-pointer whitespace-nowrap text-xs font-semibold text-secondary-500 hover:text-foreground-800"
                >
                  Não
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setDesfazerCancelarPara(f.encontro_id)}
                className="mt-1 cursor-pointer whitespace-nowrap text-sm font-semibold text-primary-600 hover:underline"
              >
                Desfazer
              </button>
            )}
          </div>
        )}

        {f.situacao === 'justificada' && (
          <div className="mt-1">
            <p className="text-sm font-medium text-info-700">Justificada</p>
            <p className="text-sm text-foreground-600">
              {motivoJustificarTexto(f.motivo, f.observacao)}
            </p>
            {desfazerPara === f.encontro_id ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-600">Desfazer a justificativa?</span>
                <Botao
                  variante="secundario"
                  className="h-9 px-3 text-xs"
                  carregando={salvando === `desf-${f.encontro_id}`}
                  onClick={() => desfazer(f.encontro_id)}
                >
                  Sim, desfazer
                </Botao>
                <button
                  type="button"
                  onClick={() => setDesfazerPara(null)}
                  className="cursor-pointer whitespace-nowrap text-xs font-semibold text-secondary-500 hover:text-foreground-800"
                >
                  Não
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setDesfazerPara(f.encontro_id)}
                className="mt-1 cursor-pointer whitespace-nowrap text-sm font-semibold text-primary-600 hover:underline"
              >
                Desfazer
              </button>
            )}
          </div>
        )}

        {ehFalta && (
          <div className="mt-1">
            <p className="flex items-center gap-1.5 text-sm font-medium text-danger-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-checkbox-blank-circle-line" />
              </span>
              Falta
            </p>
            {f.situacao === 'aguardando' && (
              <p className="mt-0.5 text-xs text-warning-700">
                Foto esperando aprovação — aprove em Por formação
              </p>
            )}
            {f.situacao === 'recusada' && (
              <p className="mt-0.5 text-xs text-danger-600">Foto recusada</p>
            )}

            {mostrar ? (
              <>
                <p className="mt-2 text-xs font-semibold text-foreground-700">Por que faltou?</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {MOTIVOS_JUSTIFICAR.map((m) => {
                    const ativo = motivos[f.encontro_id] === m.valor;
                    return (
                      <button
                        key={m.valor}
                        type="button"
                        onClick={() =>
                          setMotivos((prev) => ({ ...prev, [f.encontro_id]: m.valor }))
                        }
                        className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                          ativo
                            ? 'border-primary-400 bg-primary-50 text-primary-700'
                            : 'border-secondary-200 bg-white text-foreground-700 hover:bg-background-50'
                        }`}
                      >
                        {m.rotulo}
                      </button>
                    );
                  })}
                </div>
                {motivos[f.encontro_id] === 'outro' && (
                  <div className="mt-2">
                    <Campo
                      rotulo="Escreva o motivo"
                      value={obsJust[f.encontro_id] || ''}
                      onChange={(v) => setObsJust((prev) => ({ ...prev, [f.encontro_id]: v }))}
                      maxLength={140}
                      placeholder="Por quê?"
                    />
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Botao
                    icone="ri-check-line"
                    carregando={salvando === `just-${f.encontro_id}`}
                    onClick={() => justificar(f.encontro_id)}
                  >
                    Justificar falta
                  </Botao>
                  {!f.conferida && (
                    <Botao
                      variante="secundario"
                      icone="ri-user-follow-line"
                      carregando={salvando === `marcar-${f.encontro_id}`}
                      onClick={() => marcarPresenca(f.encontro_id)}
                    >
                      Estava lá · marcar presença
                    </Botao>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                <Botao
                  variante="secundario"
                  icone="ri-error-warning-line"
                  onClick={() =>
                    setMostrarMotivos((prev) => ({ ...prev, [f.encontro_id]: true }))
                  }
                >
                  Por que faltou?
                </Botao>
                {!f.conferida && (
                  <Botao
                    variante="secundario"
                    icone="ri-user-follow-line"
                    carregando={salvando === `marcar-${f.encontro_id}`}
                    onClick={() => marcarPresenca(f.encontro_id)}
                  >
                    Estava lá · marcar presença
                  </Botao>
                )}
              </div>
            )}
          </div>
        )}

        {f.situacao === 'futura' && (
          <p className="mt-1 text-sm text-secondary-500">Ainda vai acontecer</p>
        )}
      </div>
    );
  }

  const wa = linkWhatsapp(pessoa.telefone);

  return (
    <>
      <Modal aberto titulo="Frequência" aoFechar={aoFechar}>
      {/* 1 · quem é */}
      <div className="flex items-start gap-4">
        {fotoUrl ? (
          <button
            type="button"
            onClick={() => setFotoGrande(true)}
            aria-label="Ver foto maior"
            className="shrink-0 cursor-pointer"
          >
            <img
              src={fotoUrl}
              alt={pessoa.nome}
              className="rounded-full border-2 border-secondary-200 object-cover"
              style={{ height: 88, width: 88 }}
            />
          </button>
        ) : (
          <div
            className="flex shrink-0 items-center justify-center rounded-full bg-secondary-100 text-2xl font-semibold text-secondary-500"
            style={{ height: 88, width: 88 }}
          >
            {iniciaisDe(pessoa.nome)}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-bold text-foreground-950">{pessoa.nome}</h3>
          <p className="mt-0.5 text-sm text-foreground-600">
            {pessoa.idade !== null ? `${pessoa.idade} anos` : 'Idade não informada'}
            {pessoa.telefone ? ` · ${formatarTelefone(pessoa.telefone)}` : ''}
          </p>
          {wa && (
            <Botao
              variante="whatsapp"
              icone="ri-whatsapp-line"
              className="mt-2"
              onClick={() => window.open(wa, '_blank', 'noopener,noreferrer')}
            >
              WhatsApp
            </Botao>
          )}
        </div>
      </div>

      {/* 2 · um bloco por formação */}
      <div className="mt-4 flex flex-col gap-3">
        {pessoa.formacoes.length === 0 ? (
          <p className="text-sm text-secondary-500">Nenhuma formação registrada ainda.</p>
        ) : (
          pessoa.formacoes.map((f) => blocoFormacao(f))
        )}
      </div>

      {/* 3 · pode servir? */}
      <div className="mt-5 rounded-2xl border border-secondary-100 bg-background-50 p-3">
        <p className="text-sm font-semibold text-foreground-950">Pode servir nas equipes?</p>

        {pessoa.fora_da_montagem ? (
          <div className="mt-2">
            <p className="text-sm text-foreground-600">
              Fora da montagem desde {ddmm(pessoa.fora_desde)} ·{' '}
              {motivoServirTexto(pessoa.fora_motivo, pessoa.fora_observacao)}
              {pessoa.fora_por ? ` · por ${pessoa.fora_por}` : ''}
            </p>
            <Botao
              icone="ri-user-add-line"
              className="mt-3"
              carregando={salvando === 'servir-on'}
              onClick={() => servir(true)}
            >
              Voltar para a montagem
            </Botao>
          </div>
        ) : tirarAberto ? (
          <div className="mt-2">
            <div className="flex flex-col gap-2">
              {OPCOES_SERVIR.map((o) => {
                const ativo = motivoServir === o.valor;
                return (
                  <button
                    key={o.valor}
                    type="button"
                    onClick={() => setMotivoServir(o.valor)}
                    className={`flex cursor-pointer items-center gap-2 rounded-2xl border px-3 py-2.5 text-left text-sm font-semibold transition-colors ${
                      ativo
                        ? 'border-primary-400 bg-primary-50 text-primary-700'
                        : 'border-secondary-200 bg-white text-foreground-800 hover:bg-background-50'
                    }`}
                  >
                    <span className="flex h-5 w-5 items-center justify-center">
                      <i
                        className={
                          ativo ? 'ri-radio-button-line' : 'ri-checkbox-blank-circle-line'
                        }
                      />
                    </span>
                    {o.rotulo}
                  </button>
                );
              })}
            </div>
            {motivoServir === 'outro' && (
              <div className="mt-2">
                <Campo
                  rotulo="Escreva o motivo"
                  value={obsServir}
                  onChange={setObsServir}
                  maxLength={140}
                  placeholder="Por quê?"
                />
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <Botao
                variante="perigo"
                className="flex-1"
                carregando={salvando === 'servir-off'}
                onClick={() => servir(false)}
              >
                Confirmar
              </Botao>
              <Botao
                variante="secundario"
                className="flex-1"
                onClick={() => {
                  setTirarAberto(false);
                  setMotivoServir(null);
                  setObsServir('');
                }}
              >
                Cancelar
              </Botao>
            </div>
          </div>
        ) : (
          <Botao
            variante="perigo-contorno"
            icone="ri-user-unfollow-line"
            className="mt-3"
            onClick={() => setTirarAberto(true)}
          >
            Tirar da montagem
          </Botao>
        )}

        <p className="mt-3 rounded-xl bg-warning-50 p-2.5 text-xs leading-relaxed text-warning-800">
          Tirar da montagem não cancela a inscrição: pagamentos, talão da ação entre amigos e histórico
          continuam. A pessoa só deixa de aparecer na montagem de equipes e nos lembretes. Volta
          com um toque.
        </p>
      </div>

      {/* 4 · histórico */}
      <div className="mt-5 border-t border-secondary-100 pt-3">
        <button
          type="button"
          onClick={abrirHistorico}
          className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-primary-600 hover:underline"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-history-line" />
          </span>
          Ver histórico
        </button>
        {historicoAberto && (
          <div className="mt-2 flex flex-col gap-1.5">
            {historico === null ? (
              <p className="text-sm text-secondary-500">Carregando…</p>
            ) : historico.length === 0 ? (
              <p className="text-sm text-secondary-500">Sem histórico ainda.</p>
            ) : (
              historico.map((h, i) => (
                <p key={`${h.quando}-${i}`} className="text-xs text-foreground-600">
                  <span className="font-semibold text-foreground-800">
                    {ddmm(h.quando)} {hhmm(h.quando)}
                  </span>{' '}
                  · {h.o_que}
                  {h.detalhe ? ` · ${h.detalhe}` : ''}
                  {h.quem ? ` · ${h.quem}` : ''}
                </p>
              ))
            )}
          </div>
        )}
      </div>
      </Modal>

      {cancelando && (
        <FolhaCancelarPresencaFrequencia
          nome={pessoa.nome}
          quemMarcou={textoQuemMarcou(cancelando)}
          salvando={salvandoCancel}
          erro={erroCancel}
          aoVoltar={() => setCancelando(null)}
          aoConfirmar={cancelarPresenca}
        />
      )}

      {fotoGrande && fotoUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80"
            onClick={() => setFotoGrande(false)}
            aria-hidden
          />
          <button
            type="button"
            onClick={() => setFotoGrande(false)}
            aria-label="Fechar foto"
            className="absolute right-4 top-4 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-white/90 text-foreground-900 transition-colors hover:bg-white"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <img
            src={fotoUrl}
            alt={pessoa.nome}
            className="relative max-h-[85vh] max-w-full rounded-lg object-contain"
          />
        </div>
      )}
    </>
  );
}